import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  MessageCircle,
  Phone,
  Mail,
  User as UserIcon,
  GraduationCap,
  MapPin,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const ACADEMY_PHONE = "919968110261";

function buildWhatsAppLink(lead, course) {
  const lines = [
    `Hi Percept Academy, I'd like to enrol${course ? ` in *${course.title}*` : ""}.`,
    "",
    `*Name:* ${lead.name}`,
    `*Phone:* ${lead.phone}`,
  ];
  if (lead.email) lines.push(`*Email:* ${lead.email}`);
  if (lead.class_or_age) lines.push(`*Class / Age:* ${lead.class_or_age}`);
  if (lead.city) lines.push(`*City:* ${lead.city}`);
  if (lead.message) lines.push(`*Message:* ${lead.message}`);
  const text = encodeURIComponent(lines.join("\n"));
  return `https://wa.me/${ACADEMY_PHONE}?text=${text}`;
}

function isValidPhone(p) {
  const digits = (p || "").replace(/[^\d+]/g, "");
  const trimmed = digits.replace(/^\+/, "");
  return trimmed.length >= 8 && trimmed.length <= 15;
}

export default function Enroll() {
  const [searchParams] = useSearchParams();
  const courseId = searchParams.get("course");
  const navigate = useNavigate();

  const [course, setCourse] = useState(null);
  const [allCourses, setAllCourses] = useState([]);
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    class_or_age: "",
    city: "",
    message: "",
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [success, setSuccess] = useState(null);

  useEffect(() => {
    api.get("/courses").then((r) => {
      setAllCourses(r.data);
      if (courseId) {
        const c = r.data.find((x) => x.id === courseId);
        if (c) setCourse(c);
      }
    }).catch(() => {});
  }, [courseId]);

  async function submit(e) {
    e.preventDefault();
    setErr("");
    if (!form.name.trim()) return setErr("Please enter your name.");
    if (!isValidPhone(form.phone))
      return setErr("Please enter a valid phone number (8–15 digits).");
    setBusy(true);
    try {
      const payload = {
        ...form,
        course_id: course?.id || null,
        course_title: course?.title || null,
      };
      const { data } = await api.post("/leads", payload);
      const waLink = buildWhatsAppLink(data, course);
      setSuccess({ lead: data, waLink });
      // Open WhatsApp in a new tab so they confirm by sending the message
      window.open(waLink, "_blank", "noopener,noreferrer");
    } catch (ex) {
      setErr(formatApiError(ex));
    } finally {
      setBusy(false);
    }
  }

  if (success) {
    return (
      <main
        data-testid="enroll-success-page"
        className="min-h-[80vh] flex items-center justify-center px-6 py-16"
      >
        <div className="max-w-xl w-full bg-white rounded-3xl brutal-shadow-lg p-8 md:p-12 text-center">
          <div className="w-16 h-16 rounded-full bg-[#D4EDDB] text-[#1F2547] flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8" />
          </div>
          <h1 className="font-display text-3xl md:text-4xl font-black tracking-tight text-[#1F2547]">
            One last step on WhatsApp
          </h1>
          <p className="mt-3 text-[#5B6079] font-medium">
            We&apos;ve opened a WhatsApp chat with your details pre-filled.{" "}
            <b>Just hit send</b> — that confirms your phone and our team will
            get back to you shortly.
          </p>

          <div className="mt-6 p-4 bg-[#EFE9F7] rounded-2xl text-left">
            <div className="text-xs font-bold uppercase tracking-[0.2em] text-[#9F87D8] mb-1">
              Your enquiry
            </div>
            <div className="font-display font-bold text-[#1F2547]">
              {success.lead.name} · {success.lead.phone}
            </div>
            {course && (
              <div className="text-sm text-[#5B6079] font-medium mt-1">
                Course: {course.title}
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3 justify-center">
            <a
              href={success.waLink}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="enroll-success-whatsapp-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#9F87D8] text-white font-bold rounded-full brutal-shadow btn-press"
            >
              <MessageCircle className="w-4 h-4" /> Reopen WhatsApp
            </a>
            <button
              onClick={() => navigate("/")}
              data-testid="enroll-success-home-btn"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 bg-[#EFE9F7] text-[#1F2547] font-bold rounded-full btn-press"
            >
              Back to home
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main data-testid="enroll-page" className="min-h-[80vh] py-12 md:py-20">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <Link
          to="/"
          data-testid="enroll-back-link"
          className="inline-flex items-center gap-2 text-sm font-bold text-[#5B6079] hover:text-[#1F2547] mb-6"
        >
          <ArrowLeft className="w-4 h-4" /> Back to home
        </Link>

        <div className="grid md:grid-cols-5 gap-8 md:gap-10 items-start">
          {/* Left column — course summary */}
          <aside className="md:col-span-2">
            <span className="text-xs font-bold uppercase tracking-[0.25em] text-[#9F87D8]">
              / Enrol with us
            </span>
            <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter text-[#1F2547] mt-3 leading-[1]">
              Tell us a little about you.
            </h1>
            <p className="mt-4 text-[#5B6079] font-medium">
              Fill the form and we&apos;ll continue the chat on WhatsApp.
              It&apos;s the fastest way to reach our teachers.
            </p>

            {course && (
              <div
                data-testid="enroll-course-summary"
                className="mt-6 rounded-2xl bg-white brutal-shadow p-5 overflow-hidden"
              >
                {course.image && (
                  <img
                    src={course.image}
                    alt={course.title}
                    className="w-full h-32 object-cover rounded-xl mb-4"
                  />
                )}
                <div className="flex items-center gap-2 mb-1">
                  {course.tag && (
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#D4EDDB] text-[#1F2547]">
                      {course.tag}
                    </span>
                  )}
                  {course.duration && (
                    <span className="text-[10px] font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-full bg-[#EFE9F7] text-[#1F2547]">
                      {course.duration}
                    </span>
                  )}
                </div>
                <h3 className="font-display font-black text-xl text-[#1F2547]">
                  {course.title}
                </h3>
                <p className="text-sm text-[#5B6079] font-medium mt-1 line-clamp-4">
                  {course.description}
                </p>
              </div>
            )}
          </aside>

          {/* Right column — form */}
          <form
            onSubmit={submit}
            data-testid="enroll-form"
            className="md:col-span-3 bg-white rounded-3xl brutal-shadow p-6 md:p-8 space-y-5"
          >
            {!course && (
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                  Course *
                </label>
                <select
                  data-testid="enroll-course-select"
                  required
                  value={courseId || ""}
                  onChange={(e) => {
                    const c = allCourses.find((x) => x.id === e.target.value);
                    setCourse(c || null);
                    navigate(`/enroll?course=${e.target.value}`, { replace: true });
                  }}
                  className="w-full px-3 py-3 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium bg-white"
                >
                  <option value="">Choose a course…</option>
                  {allCourses.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.title}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                Full name *
              </label>
              <div className="relative">
                <UserIcon className="w-4 h-4 text-[#5B6079] absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  data-testid="enroll-field-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                  placeholder="e.g. Aarav Sharma"
                />
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                  WhatsApp phone *
                </label>
                <div className="relative">
                  <Phone className="w-4 h-4 text-[#5B6079] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="enroll-field-phone"
                    type="tel"
                    required
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                    placeholder="+91 98765 43210"
                  />
                </div>
                <p className="text-[11px] text-[#5B6079] mt-1.5 font-medium">
                  We&apos;ll send a WhatsApp confirmation.
                </p>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                  Email
                </label>
                <div className="relative">
                  <Mail className="w-4 h-4 text-[#5B6079] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="enroll-field-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                    placeholder="you@example.com"
                  />
                </div>
              </div>
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                  Class / Age
                </label>
                <div className="relative">
                  <GraduationCap className="w-4 h-4 text-[#5B6079] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="enroll-field-class"
                    value={form.class_or_age}
                    onChange={(e) => setForm({ ...form, class_or_age: e.target.value })}
                    className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                    placeholder="e.g. Class 8 or Age 22"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                  City
                </label>
                <div className="relative">
                  <MapPin className="w-4 h-4 text-[#5B6079] absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    data-testid="enroll-field-city"
                    value={form.city}
                    onChange={(e) => setForm({ ...form, city: e.target.value })}
                    className="w-full pl-9 pr-3 py-3 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                    placeholder="e.g. Delhi"
                  />
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold uppercase tracking-[0.18em] text-[#5B6079] mb-1.5">
                Message (optional)
              </label>
              <textarea
                data-testid="enroll-field-message"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={3}
                className="w-full px-3 py-3 rounded-xl border border-[#1F2547]/15 focus:outline-none focus:border-[#9F87D8] text-[#1F2547] font-medium"
                placeholder="Anything you want to share with us?"
              />
            </div>

            {err && (
              <div
                data-testid="enroll-form-error"
                className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2 font-medium"
              >
                {err}
              </div>
            )}

            <button
              type="submit"
              data-testid="enroll-submit-btn"
              disabled={busy}
              className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 bg-[#9F87D8] text-white font-bold rounded-full brutal-shadow btn-press disabled:opacity-60"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />}
              Send & continue on WhatsApp
            </button>

            <p className="text-[11px] text-[#5B6079] font-medium text-center">
              By submitting, you agree we may contact you about your enquiry.
            </p>
          </form>
        </div>
      </div>
    </main>
  );
}
